import { normalizeCloudMevEnv } from "./cloudMevEnv";

/** SW-77 steps. A trailing * marks a step that needs a PDF named after the case. */
const SHEET = `
001.001
001.002
001.003
001.004
500.001
500.002
500.003
500.004
501.001
501.002
501.003
501.004
501.005
501.008
502.001
502.002
502.003
502.004
503.001*
503.202*
503.403
503.004*
503.205*
503.406
503.007*
503.208*
503.409*
503.010*
503.211*
503.412*
503.013
503.214
503.415
503.016
503.217
503.418
503.019
503.220
504.401
504.002
504.203*
504.404*
504.005
504.206
504.407
504.008
504.209
504.410
505.001*
505.202
505.403*
505.004
505.205
505.406
506.001*
506.202*
506.403
506.004
506.205
506.406
506.007
506.208
507.402
507.003
508.201*
508.402
508.003
508.204
508.405
508.006
509.201*
509.405*
509.006
509.207
509.409
510.008
510.209*
510.410
510.011
510.214
510.415*
510.016
510.217*
510.418
510.019
511.201*
511.402*
514.001*
514.202
514.403
514.004*
514.205
514.406
515.001*
515.202
515.403
515.004
515.205*
515.406*
516.001
519.201
519.402*
519.003*
519.204
519.405
520.001*
520.202*
520.403*
520.004*
521.201*
521.402*
521.003*
521.204*
521.405
521.006
522.201*
522.402*
522.003*
522.204*
522.405
522.006
524.201*
524.402*
524.003
525.201*
526.401*
526.002
527.201
529.401
530.001*
530.202*
530.403
531.001*
531.202*
531.403*
532.001*
532.202
533.401*
534.001*
535.201
536.401*
537.001
537.202
537.403
537.004
603.006*
605.001
`;

export const ESSAI_CASE_PATTERN = /^\d{3}\.\d\d{2}$/;

export type EssaiStep = { id: string; pdf: boolean };
export type EssaiCaseGroup = { caseNumber: string; steps: EssaiStep[] };

export const ESSAI_CASE_GROUPS: EssaiCaseGroup[] = SHEET.trim()
  .split(/\s+/)
  .filter(Boolean)
  .reduce<EssaiCaseGroup[]>((groups, token) => {
    const pdf = token.endsWith("*");
    const id = pdf ? token.slice(0, -1) : token;
    const caseNumber = id.slice(0, 3);
    const last = groups[groups.length - 1];
    const step = { id, pdf };
    if (!last || last.caseNumber !== caseNumber) groups.push({ caseNumber, steps: [step] });
    else last.steps.push(step);
    return groups;
  }, []);

const CASE_CHANGED = "essai-test-case";

export type EssaiTestRun = {
  folderName: string;
  startedAt: string;
  active: boolean;
};

const caseKey = (storeId?: string | null) => `essaiTestCase_${storeId || "store"}`;
const runKey = (storeId?: string | null) => `essaiTestRun_${storeId || "store"}`;

function notify() {
  window.dispatchEvent(new CustomEvent(CASE_CHANGED));
}

export function onEssaiTestChange(listener: () => void) {
  window.addEventListener(CASE_CHANGED, listener);
  window.addEventListener("cloud-mev-envirn", listener);
  return () => {
    window.removeEventListener(CASE_CHANGED, listener);
    window.removeEventListener("cloud-mev-envirn", listener);
  };
}

export function readEssaiTestCase(storeId?: string | null): string {
  try {
    const value = String(localStorage.getItem(caseKey(storeId)) || "").trim();
    return ESSAI_CASE_PATTERN.test(value) ? value : "";
  } catch {
    return "";
  }
}

export function writeEssaiTestCase(storeId: string | null | undefined, value: string) {
  const next = String(value || "").trim();
  if (!ESSAI_CASE_PATTERN.test(next)) return false;
  localStorage.setItem(caseKey(storeId), next);
  notify();
  return true;
}

export function readEssaiTestRun(storeId?: string | null): EssaiTestRun | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(runKey(storeId)) || "null");
    if (!parsed || typeof parsed.folderName !== "string") return null;
    return {
      folderName: parsed.folderName,
      startedAt: String(parsed.startedAt || ""),
      active: parsed.active !== false,
    };
  } catch {
    return null;
  }
}

export function writeEssaiTestRun(storeId: string | null | undefined, run: EssaiTestRun) {
  localStorage.setItem(runKey(storeId), JSON.stringify(run));
  notify();
}

export function stopEssaiTestRun(storeId: string | null | undefined) {
  const run = readEssaiTestRun(storeId);
  if (!run) return;
  writeEssaiTestRun(storeId, { ...run, active: false });
}

export function isEssaiTestActive(storeId?: string | null) {
  return readEssaiTestRun(storeId)?.active === true;
}

/** The case saved on the top bar for the running ESSAI test. */
function readActiveEssaiCase(storeId?: string | null): string {
  if (isEssaiTestActive(storeId)) {
    const picked = readEssaiTestCase(storeId);
    if (picked) return picked;
  }
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index) || "";
      if (!key.startsWith("essaiTestRun_")) continue;
      const id = key.slice("essaiTestRun_".length);
      if (!isEssaiTestActive(id)) continue;
      const picked = readEssaiTestCase(id);
      if (picked) return picked;
    }
  } catch {
    return "";
  }
  return "";
}

/** The only sale change: a running ESSAI test uses the case chosen on the top bar. */
export function caseForEssaiHeader(envirn: string, storeId?: string | null): string {
  if (normalizeCloudMevEnv(envirn) !== "ESSAI") return "000.000";
  return readActiveEssaiCase(storeId) || "000.000";
}
